import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { films } from "@/mocks/films";
import { emptyFilters, filterFilms, paginate, sortFilms } from "@/utils/filmQuery";
import type { FilterState, SortKey } from "@/utils/filmQuery";
import FilmCard from "@/components/feature/FilmCard";
import FilterPanel from "@/components/feature/FilterPanel";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/pages/category/components/EmptyState";

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const genreParam = searchParams.get("genre") ?? "全部";
  const sortParam = (searchParams.get("sort") as SortKey | null) ?? "newest";
  const pageParam = Number(searchParams.get("page") ?? "1");

  const [filters, setFilters] = useState<FilterState>({ ...emptyFilters, genre: genreParam });
  const [sort, setSort] = useState<SortKey>(sortParam);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const results = useMemo(
    () => sortFilms(filterFilms(films, filters), sort),
    [filters, sort],
  );

  const { items, totalPages, currentPage } = paginate(results, pageParam, PAGE_SIZE);

  const goPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page > 1) next.set("page", String(page));
    else next.delete("page");
    setSearchParams(next);
  };

  const changeSort = (next: SortKey) => {
    setSort(next);
    const params = new URLSearchParams(searchParams);
    params.set("sort", next);
    params.delete("page");
    setSearchParams(params);
  };

  const resetAll = () => {
    setFilters(emptyFilters);
    setMobilePanelOpen(false);
    setSearchParams({});
  };

  const activeGenre = filters.genre;
  const title = activeGenre === "全部" ? "全部影视作品" : `${activeGenre}片`;

  useEffect(() => {
    document.title = `${title} - 色即是空影视作品分类`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        `${title}分类页收录${results.length}部经典影视作品的剧情介绍、导演与演员资料，支持按地区、年份、评分筛选与排序浏览。`,
      );
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute("content", `${activeGenre},影视分类,${title},色即是空影视作品,剧情介绍`);
  }, [title, activeGenre, results.length]);

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-24 md:px-8 md:pb-10 md:pt-32">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视分类", to: "/category" },
              { label: title },
            ]}
          />
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-[28px] font-bold leading-tight text-foreground-950 md:text-[36px]">
                {title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {activeGenre === "全部"
                  ? "按地区、年份、评分与类型组合筛选经典影视作品，每一部都配有完整的剧情脉络、主创档案与相关推荐。"
                  : `以${activeGenre}为核心标签整理的作品合集，侧重人物关系与叙事结构的解读，适合逐步深入阅读。`}
              </p>
            </div>
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-primary-700">{results.length}</span> 部作品
              <span className="mx-2 text-background-400">|</span>
              第 {currentPage} / {totalPages} 页
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="hidden md:block">
          <FilterPanel
            filters={filters}
            onChange={(next) => {
              setFilters(next);
              goPage(1);
            }}
            sort={sort}
            onSortChange={changeSort}
          />
        </div>

        <div className="mb-4 flex items-center justify-between gap-3 md:hidden">
          <button
            type="button"
            onClick={() => setMobilePanelOpen(true)}
            className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-filter-3-line" />
            </span>
            筛选条件
          </button>
          <div className="relative">
            <select
              value={sort}
              onChange={(event) => changeSort(event.target.value as SortKey)}
              aria-label="排序方式"
              className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2.5 pl-3 pr-8 text-sm text-foreground-800 outline-none"
            >
              <option value="newest">最新上线</option>
              <option value="oldest">年份最早</option>
              <option value="rating">评分最高</option>
              <option value="popular">热度最高</option>
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState onReset={resetAll} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {items.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goPage} />
          </>
        )}
      </div>

      {mobilePanelOpen ? (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setMobilePanelOpen(false)}
            className="absolute inset-0 bg-foreground-950/45"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[86vh] animate-pop-in overflow-y-auto rounded-t-2xl bg-background-50 p-4 pb-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setMobilePanelOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={(next) => {
                setFilters(next);
                goPage(1);
              }}
              sort={sort}
              onSortChange={changeSort}
              hideSort
            />
            <button
              type="button"
              onClick={() => setMobilePanelOpen(false)}
              className="mt-4 w-full cursor-pointer rounded-md bg-accent-500 py-3 text-sm font-medium text-foreground-950"
            >
              查看 {results.length} 部作品
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}